/**
 * Backend Error Codes
 * 
 * Error code format: DSIRE_SUMO_{ErrorType}_{HTTPStatus}{SerialNumber}
 * 
 * Error Types:
 * - 00: NON_HTTP
 * - 01: CONFIGURATION
 * - 02: SECURITY_OR_PERMISSION
 * - 03: INVALID_PARAMETERS
 * - 04: NETWORK
 * - 05: NON_CLASSIFIED
 */

// Authentication & Authorization Errors (02 - Security/Permission)
export const ERROR_CODES = {
    // Authentication Errors
    INVALID_CREDENTIALS: 'DSIRE_SUMO_02_401000',
    TOKEN_EXPIRED: 'DSIRE_SUMO_02_401001',
    ACCESS_DENIED: 'DSIRE_SUMO_02_401002',
    INVALID_TOKEN: 'DSIRE_SUMO_02_401000',
    AWS_SECRET_ACCESS_DENIED: 'DSIRE_SUMO_02_403000',
    DATABRICKS_ACCESS_DENIED: 'DSIRE_SUMO_02_401000',

    // Dataset Errors (03 - Invalid Parameters)
    DATASET_NAME_CONFLICT: 'DSIRE_SUMO_03_409000',
    DATASET_NOT_FOUND: 'DSIRE_SUMO_03_404000',
    DATASET_ID_IN_WRONG_FORMAT: 'DSIRE_SUMO_03_400000',
    NOT_ABLE_TO_LIST_FILES_FROM_DATASET: 'DSIRE_SUMO_03_404001',

    // Upload Errors (03 - Invalid Parameters, 05 - Non-Classified)
    UPLOAD_SESSION_NOT_FOUND: 'DSIRE_SUMO_03_404002',
    FAILED_TO_CHECK_UPLOAD_STATUS: 'DSIRE_SUMO_05_500003',
    FAILED_TO_ABORT_UPLOAD: 'DSIRE_SUMO_05_500004',
    FAILED_TO_PUT_EVENT_TO_S3: 'DSIRE_SUMO_05_500005',
    SOME_UPLOAD_FAILED_TO_ABORT: 'DSIRE_SUMO_05_207000',

    // General Errors (05 - Non-Classified)
    VALIDATION_ERROR: 'DSIRE_SUMO_05_502000',
    UNKNOWN_ERROR: 'DSIRE_SUMO_05_502001',
    REQUEST_ERROR: 'DSIRE_SUMO_05_502000',
    NOT_EXPECTED_RESPONSE: 'DSIRE_SUMO_03_417001',
    QUERY_FAILED: 'DSIRE_SUMO_05_500000',
    QUERY_INSERT_FAILED: 'DSIRE_SUMO_05_500001',
    QUERY_UPDATE_FAILED: 'DSIRE_SUMO_05_500002',
};

/**
 * Error code categories for easier grouping
 */
export const ERROR_CATEGORIES = {
    AUTHENTICATION: [
        ERROR_CODES.INVALID_CREDENTIALS,
        ERROR_CODES.TOKEN_EXPIRED,
        ERROR_CODES.ACCESS_DENIED,
        ERROR_CODES.INVALID_TOKEN,
    ],
    DATASET: [
        ERROR_CODES.DATASET_NAME_CONFLICT,
        ERROR_CODES.DATASET_NOT_FOUND,
        ERROR_CODES.DATASET_ID_IN_WRONG_FORMAT,
        ERROR_CODES.NOT_ABLE_TO_LIST_FILES_FROM_DATASET,
    ],
    UPLOAD: [
        ERROR_CODES.UPLOAD_SESSION_NOT_FOUND,
        ERROR_CODES.FAILED_TO_CHECK_UPLOAD_STATUS,
        ERROR_CODES.FAILED_TO_ABORT_UPLOAD,
        ERROR_CODES.FAILED_TO_PUT_EVENT_TO_S3,
        ERROR_CODES.SOME_UPLOAD_FAILED_TO_ABORT,
    ],
};

/**
 * User-friendly error messages
 */
export const ERROR_MESSAGES = {
    [ERROR_CODES.DATASET_NAME_CONFLICT]: 'Dataset name already exists! Please choose a different name.',
    [ERROR_CODES.DATASET_NOT_FOUND]: 'Dataset not found.',
    [ERROR_CODES.DATASET_ID_IN_WRONG_FORMAT]: 'Invalid dataset ID format.',
    [ERROR_CODES.INVALID_CREDENTIALS]: 'Invalid username or password.',
    [ERROR_CODES.TOKEN_EXPIRED]: 'Your session has expired. Please log in again.',
    [ERROR_CODES.ACCESS_DENIED]: 'Access denied.',
    [ERROR_CODES.UPLOAD_SESSION_NOT_FOUND]: 'Upload session not found.',
    [ERROR_CODES.FAILED_TO_CHECK_UPLOAD_STATUS]: 'Failed to check upload status.',
    [ERROR_CODES.FAILED_TO_ABORT_UPLOAD]: 'Failed to abort upload.',
};

/**
 * Helper function to get user-friendly error message
 * @param {string} errorCode - Error code from backend
 * @param {string} fallbackMessage - Fallback message if code not found
 * @returns {string} User-friendly error message
 */
export const getErrorMessage = (errorCode, fallbackMessage = 'An error occurred') => {
    return ERROR_MESSAGES[errorCode] || fallbackMessage;
};

/**
 * Helper function to check if error is in a specific category
 * @param {string} errorCode - Error code from backend
 * @param {string} category - Category name (AUTHENTICATION, DATASET, UPLOAD)
 * @returns {boolean}
 */
export const isErrorInCategory = (errorCode, category) => {
    return ERROR_CATEGORIES[category]?.includes(errorCode) || false;
};
